import { forwardRef } from 'react';
import styled from '@happymoneyinc/styled-components';
import PropTypes from 'prop-types';
import ThemeWrapper from '../utils/ThemeWrapper/index.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import Paragraph from '../Paragraph/Paragraph.js';

const Radio = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    ariaLabel,
    className,
    disabled,
    id,
    labelText,
    name,
    onChange,
    required,
    selected_id,
    testID
  } = props;
  const handleOnChange = e => {
    if (onChange) onChange(e.target, id);
  };
  return /*#__PURE__*/jsx(ThemeWrapper, {
    children: /*#__PURE__*/jsxs(RadioWrapper, {
      disabled: disabled,
      className: className,
      children: [/*#__PURE__*/jsx(StyledRadio, {
        ref: ref,
        "aria-label": ariaLabel || labelText,
        "data-testid": testID,
        disabled: disabled,
        id: id,
        name: name,
        required: required,
        type: "radio",
        onChange: handleOnChange,
        checked: selected_id ? selected_id === id : undefined
      }), /*#__PURE__*/jsx(StyledLabel, {
        disabled: disabled,
        htmlFor: id,
        children: /*#__PURE__*/jsx(StyledP, {
          muted: disabled,
          children: labelText
        })
      }), /*#__PURE__*/jsx(FocusBorder, {})]
    })
  });
});
const RadioWrapper = styled.div`
  position: relative;
  align-items: center;
  display: flex;
  margin: 0 0 ${_ref => {
  let {
    theme
  } = _ref;
  return theme.spacing[2];
}};
  cursor: pointer;
  pointer-events: ${_ref2 => {
  let {
    disabled
  } = _ref2;
  return disabled ? 'none' : 'auto';
}};
`;
const StyledLabel = styled.label`
  margin: 0;

  @media screen and (prefers-reduced-motion: reduce) {
&::before {
    position: absolute;
    top: 50%;
    left: 0;
    box-sizing: border-box;
    display: inline-block;
    width: 24px;
    height: 24px;
    content: '';
    background-color: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.colors.white;
}};
    border: ${_ref4 => {
  let {
    disabled,
    theme
  } = _ref4;
  return disabled ? `2px solid ${theme.colors.gray[3]}` : `2px solid ${theme.colors.black}`;
}};
    border-radius: 50%;
    transform: translateY(-50%);
    transition: none;
    pointer-events: none;
}
}

  &::before {
    position: absolute;
    top: 50%;
    left: 0;
    box-sizing: border-box;
    display: inline-block;
    width: 24px;
    height: 24px;
    content: '';
    background-color: ${_ref5 => {
  let {
    theme
  } = _ref5;
  return theme.colors.white;
}};
    border: ${_ref6 => {
  let {
    disabled,
    theme
  } = _ref6;
  return disabled ? `2px solid ${theme.colors.gray[3]}` : `2px solid ${theme.colors.black}`;
}};
    border-radius: 50%;
    transform: translateY(-50%);
    transition: all 0.2s ease-in;
    pointer-events: none;
  }

  &::after {
    position: absolute;
    top: 50%;
    left: 7px;
    box-sizing: border-box;
    display: inline-block;
    width: 10px;
    height: 10px;
    content: '';
    background-color: ${_ref7 => {
  let {
    theme
  } = _ref7;
  return theme.colors.white;
}};
    border-radius: 50%;
    transform: translateY(-50%);
    opacity: 0;
    pointer-events: none;
  }
`;
const FocusBorder = styled.div`@media screen and (prefers-reduced-motion: reduce) {
&::before {
    position: absolute;
    top: 50%;
    left: -5px;
    box-sizing: border-box;
    box-sizing: content-box;
    width: 24px;
    height: 24px;
    padding: 4px;
    content: '';
    background-color: transparent;
    border: ${_ref8 => {
  let {
    theme
  } = _ref8;
  return `1px dashed ${theme.colors.flamingo[1]}`;
}};
    border-radius: 50%;
    transform: translateY(-50%);
    transition: none;
    opacity: 0;
}
}

&::before {
    position: absolute;
    top: 50%;
    left: -5px;
    box-sizing: border-box;
    box-sizing: content-box;
    width: 24px;
    height: 24px;
    padding: 4px;
    content: '';
    background-color: transparent;
    border: ${_ref9 => {
  let {
    theme
  } = _ref9;
  return `1px dashed ${theme.colors.flamingo[1]}`;
}};
    border-radius: 50%;
    transform: translateY(-50%);
    transition: all 0.2s ease-in;
    opacity: 0;
  }
`;
const StyledRadio = styled.input`
  position: relative;
  z-index: 1;
  width: 24px;
  height: 24px;
  margin: 0;
  cursor: pointer;
  opacity: 0;

  &:checked {
    + ${StyledLabel} {
      &::before {
        background: ${_ref10 => {
  let {
    disabled,
    theme
  } = _ref10;
  return disabled ? theme.colors.gray[4] : theme.colors.flamingo[1];
}};
      }

      &::after {
        background: ${_ref11 => {
  let {
    theme
  } = _ref11;
  return theme.colors.white;
}};
        opacity: 1;
      }
    }
  }

  &:focus {
    ~ ${FocusBorder} {
      ::before {
        opacity: 1;
      }
    }
  }
`;
const StyledP = styled(Paragraph)`
  margin: 0 0 0 ${_ref12 => {
  let {
    theme
  } = _ref12;
  return theme.spacing[4];
}};
`;
Radio.defaultProps = {
  ariaLabel: '',
  className: '',
  disabled: false,
  id: '',
  labelText: '',
  name: '',
  onChange: null,
  required: false,
  selected_id: null,
  testID: 'radio'
};
Radio.propTypes = {
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  labelText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  required: PropTypes.bool,
  selected_id: PropTypes.string,
  testID: PropTypes.string
};
var Radio$1 = Radio;

export { Radio$1 as default };
//# sourceMappingURL=Radio.js.map
